import { IAddress } from './address.interface';
import { IBase } from './base.interface';
import { ITax, ICatalogSnapshot, IPromotion, ISchedule, IVariation } from './catalog';
import { ICustomer } from './customer.interface';
import { IPaymentMethod } from './payment-method.interface';
import { IShop } from './shop.interface';

export function getOrderModeFR(orderMode: OrderMode) {
  switch (orderMode) {
    case OrderMode.DELIVERY:
      return 'Livraison';
    case OrderMode.TAKEAWAY:
      return 'Emporter';
    case OrderMode.ONSPOT:
      return 'Sur place';
    default:
      return '';
  }
}

export const isPlatformOrder = (order: Pick<IOrder, 'channel'>) => {
  const platforms = [OrderChannel.DELIVEROO, OrderChannel.UBER_EATS, OrderChannel.JUST_EAT, OrderChannel.DELICITY];

  return !!order?.channel && platforms.includes(order.channel);
};

// Adresse à afficher pour une commande : la rue de la fiche client quand elle existe (web),
// sinon l'adresse de livraison portée par la commande elle-même (plateformes via Hubrise,
// où formattedAddress peut être une chaîne vide quand la plateforme ne transmet rien).
export const getOrderDisplayAddress = (order: { customer?: { address?: { street?: string } }, address?: { formattedAddress?: string } }): string | null => {
  const customerStreet = order?.customer?.address?.street?.trim();

  if (customerStreet) {
    return customerStreet;
  }

  return order?.address?.formattedAddress?.trim() || null;
};

export interface IOrderSearchResultItem {
  type: ItemType;
  name: string;
  quantity: number;
  variationName?: string;
}

export interface IOrderSearchResult {
  _id: string;
  serviceId: string;
  channel: OrderChannel;
  mode: OrderMode;
  status: OrderStatus;
  createdAt: string;
  dueDate: string;
  total: number;
  platformCode?: string;
  name?: string;
  cancelReason?: { reason?: string };
  customer?: { _id?: string; firstname?: string; lastname?: string; phone?: string; address?: { street?: string } };
  address?: { formattedAddress?: string; informations?: string };
  payments?: { method: { name: string }; amount: number }[];
  items?: IOrderSearchResultItem[];
  service: { _id: string; fromDate: string; toDate: string };
  shop: { _id: string; publicName?: string; internalName?: string };
}

export enum OrderStatus {
  CREATED = 'created',
  COOKING = 'cooking',
  COOKED = 'cooked',
  DELIVERING = 'delivering',
  DELIVERED = 'delivered',
  CLOSED = 'closed',
  CANCELLED = 'cancelled',
}

export enum OrderChannel {
  WEBSITE = 'website',
  TICKEAT_OS = 'tickeat_os',
  UBER_EATS = 'uber_eats',
  JUST_EAT = 'just_eat',
  DELIVEROO = 'deliveroo',
  DELICITY = 'delicity',
  KIOSK = 'kiosk',
  TABLE = 'table',
}

export enum OrderMode {
  DELIVERY = 'DELIVERY',
  TAKEAWAY = 'TAKEAWAY',
  ONSPOT = 'ONSPOT',
}

export enum TransportMode {
  BIKE = 'BIKE',
  CAR = 'CAR',
  SCOOTER_50_CM3 = 'SCOOTER_50_CM3',
  SCOOTER_125_CM3 = 'SCOOTER_125_CM3',
}

export interface IOrderDeliveryMan {
  _id: string;

  firstname: string;

  lastname: string;

  transport: TransportMode;

  departureAt: any;
}

export interface IOrderTaxes {
  tax: ITax;
  rate: number;
  tvaAmount: number;
  htAmount: number;
  ttcAmount: number;
}

export interface IOrderCustomerLoyalty {
  loyaltyPoints: number;
  shops: Array<{
    _id: string;
    loyaltyPoints: number;
  }>;
}

export interface IOrderLoyalty {
  loyaltyUsed: number;
  loyaltyDiscount: number;
  customerLoyaltyBeforeOrder?: IOrderCustomerLoyalty;
  customerLoyaltyAfterOrder?: IOrderCustomerLoyalty;
}

export type IOrderCustomer = Pick<
  ICustomer,
  '_id' | 'email' | 'firstname' | 'lastname' | 'phone' | 'shops'
> & { address?: IAddress; isNewCustomer?: boolean; birthday?: any };
export type IOrderShop = Pick<
  IShop,
  '_id' | 'publicName' | 'internalName' | 'address' | 'phone' | 'siret'
>;

export interface IOrderDeliveryMan {
  _id: string;
  firstname: string;
  lastname: string;
  transport: TransportMode;
  departureAt: any;
}

export interface IOrderTaxes {
  tax: ITax;
  rate: number;
  tvaAmount: number;
  htAmount: number;
  ttcAmount: number;
}

export interface IOrderCustomerLoyalty {
  loyaltyPoints: number;
  shops: Array<{
    _id: string;
    loyaltyPoints: number;
  }>;
}

export interface IOrderLoyalty {
  loyaltyUsed: number;
  loyaltyDiscount: number;
  customerLoyaltyBeforeOrder?: IOrderCustomerLoyalty;
  customerLoyaltyAfterOrder?: IOrderCustomerLoyalty;
}

export interface IOrderCancelReason extends IBase {
  reason: string;
}

export interface IOrderPayment {
  method: Pick<
    IPaymentMethod,
    | '_id'
    | 'name'
    | 'specialType'
    | 'negativeReturn'
    | 'openCashRegister'
    | 'authorizeCount'
    | 'disabled'
    | 'position'
  >;
  amount: number;
  quantity?: number;
  token?: string;
}

export interface IOrderPackToProductVariation {
  _id: string;
  options: IOrderOption[];
  quantity: number;
  productVariationId: string;
  productVariation?: IOrderProductVariation;

  packId: string;
}

export interface IOrderProductVariation {
  _id: string;
  name: string;
  kitchenName: string;

  price: number;
  taxId: string;

  deliveryPrice?: number;
  deliveryTaxId?: string;

  takeAwayPrice?: number;
  takeAwayTaxId?: string;

  onSpotPrice?: number;
  onSpotTaxId?: string;

  loyaltyPointOffered?: number;

  disabled?: boolean;

  productId: string;

  variation?: Pick<IVariation, '_id'>;
  variationId: string;

  accountCodeId?: string;
}

export interface IOrderOptionProduct extends Omit<IOrderProduct, 'options'> {}

export interface IOrderPackToStep {
  _id: string;
  step: IOrderStep;
}

export interface IOrderStep {
  _id: string;
  name: string;

  disabled?: boolean;

  mandatory?: boolean;

  maxChoiceCount?: number;

  stepToProductVariations: IOrderStepToProductVariation[];
}

export interface IOrderStepToProductVariation {
  _id: string;
  position?: number;

  price: number;

  quantity: number;
  productVariation: IOrderProductVariation;
  options?: IOrderOption[];

  outOfStock?: boolean;
}

export interface IOrderProduct {
  _id: string;
  name: string;
  tagsIds: string[];
  quantity: number;
  disabled?: boolean;
  productVariation: IOrderProductVariation;
  options: IOrderOption[];
}

export interface IOrderOption {
  _id: string;
  name: string;
  products: IOrderOptionProduct[];
}

export interface IOrderPack {
  _id: string;
  name: string;
  disabled?: boolean;
  tagsIds: string[];
  quantity: number;
  price: number;
  taxId: string;
  deliveryPrice?: number;
  deliveryTaxId?: string;
  takeAwayPrice?: number;
  takeAwayTaxId?: string;
  onSpotPrice?: number;
  onSpotTaxId?: string;
  loyaltyPointOffered: number;
  packToProductVariations: IOrderPackToProductVariation[];
  packToSteps: IOrderPackToStep[];
  accountCodeId: string;
  availabilitySchedule?: ISchedule;
}

export interface IOrderItem {
  type: ItemType;
  referenceId: string;
  observations?: string;
  item: IOrderProduct | IOrderPack;

  // Quantité de l'item (pour le moment ce champ est dans l'item directement, il faura changer ça)
  quantity?: number;

  // Le prix de l'item selon le mode de commande et après overrides
  unitPrice?: number;

  // Montant total des extras (options | prix sur les étapes ) x quantity
  extrasTotal?: number;

  // Total de base (unitPrice x quantity) sans les extras
  baseTotal?: number;

  // Sous-total avant remises (unitPrice + extrasTotal) x quantity
  subtotal?: number;

  // Montant total des remises appliquées (remises, promotions, etc ...)
  discountTotal?: number;

  // Montant final de la ligne (subtotal - discountTotal)
  total?: number;
}

export enum ItemType {
  PRODUCT = 'product',
  PACK = 'pack',
}

export enum DiscountType {
  PERCENTAGE = 'PERCENTAGE',
  FIXED = 'FIXED',
}

export interface IOrderDiscount {
  type: DiscountType;
  amount: number;
}

interface ItemDiscount {
  referenceId: string;
  unitPrice: number;
  discount: number;
  quantity: number;
  discountedPrice: number;
}

export interface IOrderPromotion extends Partial<IPromotion> {
  _id: string;
  orderDiscount?: number;
  itemsDiscounts?: ItemDiscount[];
  totalDiscount?: number;
  totalItemsDiscount?: number;
  computedDiscount?: number;
}

export interface IOrder extends IBase {
  appId: string;
  name: string;
  hubriseOrderId?: string;
  serviceId: string;
  platformCode?: string;
  platformPhoneAccessCode?: string;
  catalogSnapshot: Pick<ICatalogSnapshot, '_id'>;
  shop: IOrderShop;
  customer?: IOrderCustomer;
  status: OrderStatus;
  channel: OrderChannel;
  informations: string;
  mode: OrderMode;
  payments: IOrderPayment[];
  dueDate: string;
  actualDate: string;
  // deliveryZone: Pick<IDeliveryZone, '_id' | 'extraDeliveryTime' | 'fees' | 'minPrice'>;
  address?: Partial<Omit<IAddress, '_id' | 'createdAt' | 'updatedAt'>>;
  total: number;
  subtotal: number;
  deliveryFees?: number;
  items: IOrderItem[];
  loyalty: IOrderLoyalty;
  deliveryMan: IOrderDeliveryMan;
  promotions: IOrderPromotion[];
  discounts: IOrderDiscount[];
  taxes: ITax[];
  cancelReason?: IOrderCancelReason;
  strictDueDate?: boolean;
  intendedPaymentMethodIds?: string[];
  kdsSteps?: number;

  // Vente d'entraînement (chantier 9c-A) : jamais comptée, relâchable par un
  // administrateur (9c-B).
  fiscalEcole?: boolean;
}
